import { Grid, Stack } from "@mui/material";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import type { ObjectFieldValues } from "../../../../../../api/types/objectTypes";
import type {
  ObjectSelectConfiguration,
  WorkflowActivityEditConfiguration,
} from "../../../../../../api/types/workflowTypes";
import FieldEdit from "../../../../../entityFields/editors/FieldEdit";
import MultiLookupField from "../../../../../entityFields/editors/MultiLookupField";
import type { FieldState } from "./changeSetEditState";
import ObjectAutocomplete from "./ObjectAutocomplete";
import type { ObjectSelectOptions } from "./objectSelectOptionsHelper";
import { getObjectSelectValue } from "./objectSelectOptionsHelper";

interface Props {
  field: FieldState;
  editConfiguration?: WorkflowActivityEditConfiguration;
  objectSelectOptions: ObjectSelectOptions;
  onFieldChange: (fieldName: string, value: unknown) => void;
  onObjectChange: (configuration: ObjectSelectConfiguration, selectedObject: ObjectFieldValues | null) => void;
}

const getObjectSelectConfiguration = (
  editConfiguration: WorkflowActivityEditConfiguration | undefined,
  fieldName: string
): ObjectSelectConfiguration | undefined =>
  editConfiguration?.objectSelectConfigurations?.find((configuration) => configuration.fieldName === fieldName);

const ChangeSetFieldEditRow = ({
  field,
  editConfiguration,
  objectSelectOptions,
  onFieldChange,
  onObjectChange,
}: Props) => {
  const objectSelectConfiguration = getObjectSelectConfiguration(editConfiguration, field.name);

  return (
    <Grid container columns={2} sx={{ alignItems: "center", mb: 1.5 }}>
      <Grid size={{ xs: 1 }}>
        <Stack direction="row">
          <TypographyTooltipEllipsis text={field.caption} />
          {field.attributes?.includes("Required") && <RequiredFieldMarker />}
        </Stack>
      </Grid>
      <Grid size={{ xs: 1 }}>
        {objectSelectConfiguration ? (
          <ObjectAutocomplete
            options={objectSelectOptions[objectSelectConfiguration.fieldName] ?? []}
            value={getObjectSelectValue(field.newValue, objectSelectConfiguration, objectSelectOptions)}
            onChange={(value) => onObjectChange(objectSelectConfiguration, value)}
            disabled={field.attributes?.includes("ReadOnly")}
            error={field.hasError}
          />
        ) : field.fieldType === "MultiLookup" && field.fieldConfiguration?.$type === "Lookup" ? (
          <MultiLookupField
            objectType={field.fieldConfiguration.objectType}
            value={field.newValue}
            label={field.caption}
            onChange={(ids) => onFieldChange(field.name, ids)}
            disabled={field.attributes?.includes("ReadOnly")}
            error={field.hasError}
          />
        ) : (
          <FieldEdit
            value={field.newValue}
            fieldName={field.name}
            fieldType={field.fieldType}
            fieldConfiguration={field.fieldConfiguration}
            fieldAttributes={field.attributes}
            onChange={onFieldChange}
            hasError={field.hasError}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default ChangeSetFieldEditRow;
